<script lang="ts">
    import { onMount, onDestroy } from "svelte";
    import SvgIcon from "$/components/SVGIcon.svelte";
    import slackFavicon from "$/assets/images/slack-icon.png";
    import { getRootDocument } from "$/utils/Utils";
    import { createEventDispatcher } from "svelte";
    import { TenantStore } from '$/stores/TenantStore';
    import CollaboratorItem from "./CollaboratorItem.svelte";

    const dispatch = createEventDispatcher();

    // ELEMENT_HEIGHT * 3 + 4px element padding bottom
    const SCROLL_HEIGHT = 192;
    const ELEMENT_HEIGHT = 48;

    export let query;
    export let updateHasSuggestions; 
    let element;
    let selectedIndex = 0;
    let filteredSlackSugestions = [];
    let filteredTopicSugestions = [];
    let disableFiltering = false;

    $: userGroupedByWorkspaces = $TenantStore ? TenantStore.groupCollaboratorsByWorkspace() : [];
    $: slackUsersGroupedByWorkspaces = userGroupedByWorkspaces.filter(
        (el) => el.id,
    );
    $: topicMembers = userGroupedByWorkspaces.find((el) => !el.id);
    $: selectedIndex, element && scrollSuggestionMenu(selectedIndex);
    $: suggestionsCount = filteredSlackSugestions.length + filteredTopicSugestions.length;
    $: updateHasSuggestions(suggestionsCount > 0);
    $: slackSugestions = slackUsersGroupedByWorkspaces.map((el) => el.members).flat();
    $: topicSugestions = topicMembers ? topicMembers.members : [];
    $: if (!disableFiltering) {

        filteredSlackSugestions = slackSugestions.filter(collaborator =>
            collaborator.username.toLowerCase().includes(query.toLowerCase())
        );
        filteredTopicSugestions = topicSugestions.filter(collaborator =>
            collaborator.username.toLowerCase().includes(query.toLowerCase())
        );
    }

    onMount(() => {
        window.addEventListener("keydown", handleKeyDown);
    });

    onDestroy(() => {
        window.removeEventListener("keydown", handleKeyDown);
    });

    function handleKeyDown(event) { 
        if (event.key === "ArrowUp") {
            upHandler();
        } else if (event.key === "ArrowDown") {
            downHandler();
        } else if (event.key === "Enter") {
            enterHandler();
        } else if (event.key === "Escape") {
           suggestionsCount = 0;
        }
    };

    const upHandler = () => {
        selectedIndex = (selectedIndex + 1) % suggestionsCount;
    };

    const downHandler = () => {
        selectedIndex =
            (selectedIndex + suggestionsCount - 1) % suggestionsCount;
    };

    const enterHandler = () => {
        const selectedCollaborator = [...filteredTopicSugestions, ...filteredSlackSugestions][selectedIndex];

        if (selectedCollaborator) {
          onSelect(selectedCollaborator);
        } 
    };

    const scrollSuggestionMenu = (index: number) => {
        const suggestionElement = getRootDocument().getElementById(
            "sugestion-" + index,
        );
        const suggestionOffsetTop = suggestionElement?.offsetTop;
        if (index + 1 === suggestionsCount) {
            //scroll to the top of the list (since we have headers for each workspace it would be complicated to calculate the
            //right height of the whole list, so we set an abnormally high value)
            scrollElementToPosition(1000000);
        } else if (suggestionOffsetTop < 0) {
            scrollElementToPosition(Math.abs(suggestionOffsetTop));
        } else {
            scrollElementToPosition(0);
        }
    };

    const scrollElementToPosition = (position) => {
        requestAnimationFrame(() => {
            element?.scrollTo({
                top: -position,
                behavior: "smooth",
            });
        });
    };

    const calculateSlackSuggestionIndex = (workspaceIndex) => {
        let suggestionIndex = 0;
        for (let i = 0; i < workspaceIndex; i++)
            suggestionIndex += slackUsersGroupedByWorkspaces[i].members?.length;

        return topicSugestions.length + suggestionIndex;
    }

    export function removeAllSuggestions() {
        const suggestions = getRootDocument().getElementById('collaborator-suggestions');
        suggestions?.remove();
    }

    function onSelect(collaborator){
        suggestionsCount = 0;
        disableFiltering = true;
        dispatch("select", collaborator.username); 
    };

</script>
{#if suggestionsCount>0}
<div class="suggestion-wrapper" id="collaborator-suggestions"  tabindex="0" on:keydown={handleKeyDown}>
    <div
        class="results"
        style="max-height:{SCROLL_HEIGHT}px"
        bind:this={element}
        on:mousedown|preventDefault
    >
        {#if filteredTopicSugestions.length}
            {#each filteredTopicSugestions as collaborator, currentCounter}
                <CollaboratorItem
                    {collaborator}
                    {selectedIndex}
                    {currentCounter}
                    {ELEMENT_HEIGHT}
                    index={undefined}       
                    workspaceIndex={undefined} 
                    calculateSlackSuggestionIndex={undefined}  
                    {onSelect}
                />
            {/each}

            {#if filteredTopicSugestions.length}
                <div class="suggestion-workspace-header">
                    <p class="workspace-slack-icon">
                        <SvgIcon name="multiple-users" size={16} />
                    </p>
                    <p class="workspace-title">
                        Topic & Cluster Members
                        <span class="suggestion-count"
                            >{filteredTopicSugestions.length}</span
                        >
                    </p>
                </div>
            {/if}
        {/if}

        {#if filteredSlackSugestions.length}
            {#each slackUsersGroupedByWorkspaces as workspace, workspaceIndex}
                {#if workspace.members.some(member => filteredSlackSugestions.includes(member))}
                    <div class="workspace-container">
                        <div class="suggestion-workspace-header">
                            <p class="workspace-slack-icon">
                                <img
                                    src={workspace.icon}
                                    class="workspace-icon"
                                    alt="{workspace.name} slack icon"
                                />
                                <img
                                    src={slackFavicon}
                                    class="slack-icon"
                                    alt="slack logo"
                                />
                            </p>
                            <p class="workspace-title">
                                {workspace.name}
                                <span class="suggestion-count">
                                    {filteredSlackSugestions.filter(m => workspace.members.includes(m)).length}
                                </span>
                            </p>
                        </div>
                        {#each workspace.members as collaborator, index}
                            {#if filteredSlackSugestions.includes(collaborator)}
                                <CollaboratorItem
                                    {collaborator}
                                    {selectedIndex}
                                    {index}          
                                    {workspaceIndex}
                                    {onSelect}
                                    {ELEMENT_HEIGHT}
                                    {calculateSlackSuggestionIndex}
                                />
                            {/if}
                        {/each}
                    </div>
                {/if}
            {/each}
        {/if}
    </div>
</div>
{/if}

<style>
    .suggestion-wrapper {
       @apply relative h-[calc(100%-10px)] overflow-y-auto overflow-x-hidden rounded-md border border-solid border-gray-200 bg-white p-1 pb-0 text-black shadow !important;
   }



   .upgrade-warning {
       @apply flex h-auto w-full shrink-0 flex-col items-center justify-center gap-1 rounded border-none bg-white p-0 text-center text-sm outline-none !important;
   }

   .upgrade-warning-title {
       @apply font-semibold !important;
   }

   .slack-text {
       @apply ml-1 font-bold !important;
   }

   .upgrade-warning-text {
       @apply text-gray-500 !important;
   }
   .slack-mention-upgrade-option {
       @apply absolute z-max hidden;
   }

   .results {
       @apply flex h-full w-full flex-col-reverse overflow-y-auto overflow-x-hidden !important;
   }

   .option-no-result {
       @apply my-1 w-full p-2 text-center text-black sm:p-0 !important;
   }

   .results::-webkit-scrollbar-thumb {
       @apply rounded-full border-l-2 border-solid border-transparent bg-clip-content !important;
   }

   .create-collaborator-button {
       @apply mb-1 w-full cursor-pointer overflow-hidden text-ellipsis whitespace-nowrap border-none p-2 text-left text-black !important;
   }

   .create-collaborator-button.active {
       @apply border-none bg-gray-100 text-sm !important;
   }

   .no-results-message {
       @apply flex h-auto flex-col items-center gap-1 p-2 text-center text-sm !important;
   }

   .no-results-message__info {
       @apply font-semibold !important;
   }

   .no-results-message__additional-info {
       @apply text-gray-500 !important;
   }

   .upgrade-button {
       @apply reset rounded-full bg-yellow-300 px-4 py-2 text-xs tracking-tight text-black transition-all hover:scale-105 hover:bg-yellow-400 !important;
   }

   .suggestion-section-header {
       @apply m-0 flex flex-row items-center rounded-md bg-gray-200 px-3 py-1.5 text-sm !important;
   }

   .suggestion-workspace-header {
       @apply m-0 mb-1 flex flex-row items-center rounded-md px-2 py-1 text-sm !important;
       background-color: #e5e7eb !important;
       color: #717886 !important;
   }

   .workspace-title {
       @apply m-0 ml-1.5 flex w-full gap-1 text-sm !important;
   }

   .workspace-icon {
       @apply h-4 w-4 rounded-full align-middle !important;
   }

   .slack-icon {
       @apply absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-white !important;
   }

   .workspace-slack-icon {
       @apply relative m-0 flex !important;
   }

   .suggestion-count {
       @apply ml-auto flex h-5 min-w-5 items-center justify-center rounded-sm p-0.5 text-xs text-gray-400 hover:bg-gray-300 hover:text-gray-500 !important;
   }

   .slack-favicon {
       @apply ml-1 h-4 w-4 !important;
   }

   .close-button {
       @apply reset absolute right-1 top-1 text-gray-500 !important;
   }

   .workspace-name {
       @apply ml-1 flex-shrink-0 text-gray-400 !important;
   }

</style>
